/** The path every Infinity client API endpoint starts with. */
const API_PREFIX = '/api/client/v2';
/** A fixed path segment chosen by client code, such as `request_token`. */
const ACTION_SEGMENT = /^[a-z][a-z_]*(?:\.jpe?g)?$/;
/**
 * The placeholder for the segment after each collection, which holds a value
 * such as a conference alias or participant UUID.
 */
const IDENTIFIER_AFTER = {
    conferences: '{conferenceAlias}',
    registrations: '{alias}',
    participants: '{participantUuid}',
    calls: '{callUuid}',
    breakouts: '{breakoutUuid}',
    video_mixes: '{mixName}',
    theme: '{themePath}',
};
/** Fixed segments that may follow a collection instead of an identifier. */
const ACTIONS_AFTER = {
    participants: new Set(['breakout']),
    breakouts: new Set(['disconnect', 'empty', 'message']),
    video_mixes: new Set(['create']),
};
/**
 * Describes an Infinity client API URL by its endpoint template, for
 * diagnostics. The host, query, fragment, and every identifier, such as the
 * conference alias, are left out, so the result contains only fixed text
 * chosen by client code. Everything after `theme/` is one `{themePath}`.
 *
 * @returns The template, or `undefined` for a URL that is not a recognized
 * Infinity client API endpoint, which is then omitted rather than logged.
 *
 * @example
 * diagnosticEndpoint('https://px01.example.com/api/client/v2/conferences/meet%40example.com/participants/p-1/dtmf?x=1');
 * // '/api/client/v2/conferences/{conferenceAlias}/participants/{participantUuid}/dtmf'
 * diagnosticEndpoint('https://cdn.example.com/branding/manifest.json');
 * // undefined
 */
export const diagnosticEndpoint = (url) => {
    let pathname;
    try {
        pathname = new URL(url, 'https://infinity.invalid').pathname;
    }
    catch {
        return undefined;
    }
    if (!pathname.startsWith(`${API_PREFIX}/`)) {
        return undefined;
    }
    const segments = pathname.slice(API_PREFIX.length + 1).split('/');
    const [collection] = segments;
    if (collection !== 'conferences' && collection !== 'registrations') {
        return undefined;
    }
    const template = [API_PREFIX];
    let previous;
    for (const [index, segment] of segments.entries()) {
        const identifier = previous === undefined ? undefined : IDENTIFIER_AFTER[previous];
        const isLast = index === segments.length - 1;
        if (segment === '' && isLast && index > 0) {
            // A trailing slash, as in `theme/`.
            template.push('');
            break;
        }
        if (identifier !== undefined &&
            !(previous !== undefined && ACTIONS_AFTER[previous]?.has(segment))) {
            template.push(identifier);
            if (previous === 'theme') {
                break;
            }
            previous = undefined;
            continue;
        }
        if (!ACTION_SEGMENT.test(segment)) {
            return undefined;
        }
        template.push(segment);
        previous = segment;
    }
    return template.join('/');
};
const DIAGNOSTIC_METHODS = new Set([
    'GET',
    'HEAD',
    'POST',
    'PUT',
    'PATCH',
    'DELETE',
    'OPTIONS',
]);
/**
 * The request method in upper case when it is a standard HTTP method, for
 * diagnostics; `undefined` otherwise, so arbitrary text is never logged.
 *
 * @example
 * diagnosticMethod('post'); // 'POST'
 * diagnosticMethod('custom'); // undefined
 */
export const diagnosticMethod = (method) => {
    const upper = method.toUpperCase();
    return DIAGNOSTIC_METHODS.has(upper) ? upper : undefined;
};
//# sourceMappingURL=diagnosticEndpoint.js.map